import { ContentAlignment, FormJourneyQuestionType, JourneyItemMediaPosition, Media } from "@plura/shared/types";
import React, { useEffect, useRef, useState } from 'react';
import MediaDisplay from "../MediaDisplay";

import './conversation-step-layout.css';
import { SwitchTransition, CSSTransition } from "react-transition-group";
import { cn } from "@plura/shared/utils";

interface Props {
  media?: Media | null;
  mediaPosition: JourneyItemMediaPosition;
  mobileMediaPosition: JourneyItemMediaPosition;
  mediOpacity: number;
  children: React.ReactNode;
  progress: number;
  editorMode?: boolean;
  currentStepType?: FormJourneyQuestionType
  contentAlignment: ContentAlignment
  duration: number;
  highFetchPriority?: boolean;
}

const ConversationStepWrapper = ({ children, media, mediaPosition, mobileMediaPosition, mediOpacity, progress, currentStepType, duration, highFetchPriority }: Props) => {
  const transitionNodeRef = useRef<HTMLDivElement>(null);
  const [classNameState, setClassNameState] = useState('animateIn');
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [shouldRenderTransition, setShouldRenderTransition] = useState(false);

  const shouldShowMediaElement = (
    mediaPosition === 'FULL' ||
    mediaPosition === 'RANDOM' ||
    mobileMediaPosition === 'MOBILE_FULL' ||
    mediaPosition === 'SPLIT_LEFT' ||
    mediaPosition === 'SPLIT_RIGHT' ||
    mediaPosition === 'DETATCHED_LEFT' ||
    mediaPosition === 'DETATCHED_RIGHT'
  ) && media;

  useEffect(() => {
    setClassNameState('animateOut');
    setMediaLoaded(false);
  }, [media?.id])

  useEffect(() => {
    if (shouldShowMediaElement) {
      setShouldRenderTransition(true);
      return undefined;
    }
    const timer = setTimeout(() => {
      setShouldRenderTransition(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [shouldShowMediaElement]);

  const handleMediaLoad = () => {
    setMediaLoaded(true);
  };

  const shouldShowMedia = mediaLoaded;

  return (
    <div className="plura-conversation-step">
      <div
        className={cn(
          'plura-conversation-step-progress',
          (currentStepType === 'END_SCREEN' || currentStepType === 'WELCOME_SCREEN') && 'plura-conversation-step-progress--hidden',
        )}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
      {shouldRenderTransition ? (
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={`${media?.id ?? 'no-thumbnail'}-${shouldShowMediaElement ? 'show' : 'hide'}`}
            nodeRef={transitionNodeRef}
            timeout={duration}
            classNames={classNameState}
            in={shouldShowMedia && !!shouldShowMediaElement}
          >
            <div ref={transitionNodeRef} className="plura-conversation-step-media-shell">
              {shouldShowMediaElement ? (
                <aside
                  className={cn(
                    'plura-conversation-step-bg-media absolute inset-0 z-0 flex h-full w-full items-center overflow-hidden',
                    mediaPosition,
                    mobileMediaPosition
                  )}
                >
                  <MediaDisplay
                    mediOpacity={mediOpacity}
                    media={media}
                    onLoadCallback={handleMediaLoad}
                    highFetchPriority={highFetchPriority}
                  />
                </aside>
              ) : (
                <span />
              )}
            </div>
          </CSSTransition>
        </SwitchTransition>
      ) : null}

      <main
        id="conversation-step-wrapper"
        className={cn(
          (currentStepType === 'END_SCREEN' || currentStepType === 'WELCOME_SCREEN') && 'plura-conversation-step-inner--center-on-mobile',
        )}
      >
        {children}
      </main>
    </div>
  )
}

export default ConversationStepWrapper
